import React, { useEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';

import { useTheme } from '@cobalt/react-theme-provider';
import Flex from '@cobalt/react-flex';
import FakeInput from '@cobalt/react-fake-input';
import Icon from '@cobalt/react-icon';
import Input from '@cobalt/react-input';

import { SearchInputProps } from './interface';

const SearchInput = (props: SearchInputProps) => {
  const {
    dataPrefix,
    size = 'medium',
    disabled = false,
    readOnly = false,
    value = '',
    placeholder = '',
    triggerDelay = 300,
    allowClear = false,
    onSearch = () => {},
    onChange,
    onBlur,
    onFocus,
    onEnter,
    onClick,
    onClear,
  } = props;
  const theme = useTheme();

  const [searchValue, setSearchValue] = React.useState('');

  const delayedQuery = useMemo(() => debounce((q) => onSearch(q), triggerDelay), [onSearch, triggerDelay]);

  const onChangeHandler = (value: string) => {
    setSearchValue(value);
    delayedQuery(value);
  };

  const clearSearchValue = () => {
    setSearchValue('');
    onChange?.('');
    onClear?.();
  };

  const handleEnter = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter') return;
    onEnter?.(searchValue);
  };

  useEffect(() => {
    setSearchValue(value);
  }, [value]);

  return (
    <FakeInput data-bi={dataPrefix} size={size} disabled={disabled} readOnly={readOnly}>
      <Flex alignY="center" height="100%" width="100%">
        <Flex padding="1">
          <Icon name="search" size="tiny" />
        </Flex>

        <Input
          data-testid="searchInput"
          aria-label={searchValue}
          variation="unstyled"
          value={searchValue}
          placeholder={placeholder}
          onChange={({ target: { value } }) => onChange?.(value) || onChangeHandler(value)}
          size={size}
          readOnly={readOnly}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyUp={handleEnter}
          onClick={onClick}
        />

        {allowClear && searchValue && (
          <Icon
            name="close"
            size={size === 'small' ? 'micro' : 'tiny'}
            color={theme.gray700}
            onClick={clearSearchValue}
          />
        )}
      </Flex>
    </FakeInput>
  );
};
export default SearchInput;
